'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Mail, Phone, ArrowRight, Loader2, ShieldCheck,
  Eye, EyeOff, CheckCircle, User, Briefcase, Lock, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '../../../lib/supabase/client';

type Role = 'employer' | 'worker';
type Step = 'role' | 'identifier' | 'otp' | 'success';
type IdentifierType = 'email' | 'phone';

interface IdentifierForm { identifier: string; }
interface OTPForm { otp: string; }

export default function AuthContent() {
  const router = useRouter();
  const [role, setRole] = useState<Role>('employer');
  const [step, setStep] = useState<Step>('role');
  const [identifierType, setIdentifierType] = useState<IdentifierType>('email');
  const [submittedIdentifier, setSubmittedIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpError, setOtpError] = useState('');

  const identifierForm = useForm<IdentifierForm>();
  const otpForm = useForm<OTPForm>();

  const handleSendOtp = async (data: IdentifierForm) => {
    setLoading(true);
    try {
      const supabase = createClient();

      if (identifierType === 'email') {
        const { error } = await supabase.functions.invoke('send-otp', {
          body: { email: data.identifier, role, type: 'magiclink' },
        });
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          phone: data.identifier,
          options: { data: { role } },
        });
        if (error) throw new Error(error.message);
      }

      setSubmittedIdentifier(data.identifier);
      setStep('otp');
      toast.success('One-time code sent!', {
        description: identifierType === 'email' ? 'Check your inbox for the sign-in link.' : 'Check your phone for the 6-digit code.',
      });
    } catch (err: any) {
      toast.error('Failed to send code', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (data: OTPForm) => {
    setLoading(true);
    setOtpError('');
    try {
      const supabase = createClient();

      if (identifierType === 'phone') {
        const { error } = await supabase.auth.verifyOtp({
          phone: submittedIdentifier,
          token: data.otp,
          type: 'sms',
        });
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.auth.verifyOtp({
          email: submittedIdentifier,
          token: data.otp,
          type: 'email',
        });
        if (error) throw new Error(error.message);
      }

      setStep('success');
      setTimeout(() => {
        router.push(role === 'worker' ? '/worker-dashboard' : '/employer-dashboard');
      }, 1500);
    } catch (err: any) {
      setOtpError(err.message || 'Invalid or expired code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!submittedIdentifier) return;
    setLoading(true);
    try {
      const supabase = createClient();
      if (identifierType === 'email') {
        const { error } = await supabase.functions.invoke('send-otp', {
          body: { email: submittedIdentifier, role, type: 'magiclink' },
        });
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.auth.signInWithOtp({
          phone: submittedIdentifier,
          options: { data: { role } },
        });
        if (error) throw new Error(error.message);
      }
      toast.success('Code resent — check your inbox.');
    } catch (err: any) {
      toast.error('Failed to resend', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--muted)' }}>
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-[45%] xl:w-[40%] flex-col justify-between p-10 xl:p-14"
        style={{ background: 'var(--charcoal)' }}
      >
        <Link href="/" className="flex items-center gap-2">
          <AppLogo size={36} />
          <span className="font-extrabold text-xl text-white">Handlenders</span>
        </Link>

        <div>
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--amber)' }}>
            Talent Network
          </p>
          <h1 className="text-3xl xl:text-4xl font-bold leading-tight mb-4 text-white" style={{ fontFamily: 'var(--font-serif)' }}>
            Attitude First.<br />
            <span style={{ color: 'var(--amber)' }}>Opportunity Follows.</span>
          </h1>
          <p className="text-white/60 text-base leading-relaxed mb-8">
            South Africa&apos;s trusted work-readiness platform. Connecting verified, assessed candidates with employers who hire with confidence.
          </p>

          <div className="space-y-3 mb-8">
            {[
              { icon: <ShieldCheck size={16} />, text: 'Verified identity — ID checked against Home Affairs' },
              { icon: <Star size={16} />, text: 'Work-Readiness Score across 5 dimensions' },
              { icon: <Lock size={16} />, text: 'Passwordless sign-in — no passwords to remember' },
              { icon: <CheckCircle size={16} />, text: 'POPIA compliant — your data stays private' },
            ].map((point, i) => (
              <div key={`trust-${i}`} className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(229,192,39,0.15)', color: 'var(--amber)' }}
                >
                  {point.icon}
                </div>
                <span className="text-sm text-white/70">{point.text}</span>
              </div>
            ))}
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: '2,400+', label: 'Candidates' },
              { value: '180+', label: 'Employers' },
              { value: '94%', label: 'Hire rate' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="text-lg font-bold" style={{ color: 'var(--amber)' }}>{stat.value}</div>
                <div className="text-xs text-white/50">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-white/25">&copy; 2026 Handlenders (Pty) Ltd · South Africa · POPIA Compliant</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 overflow-y-auto">
        {/* Mobile logo */}
        <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
          <AppLogo size={32} />
          <span className="font-extrabold text-lg" style={{ color: 'var(--charcoal)' }}>Handlenders</span>
        </Link>

        <div className="w-full max-w-md">
          {/* Step: Role selection */}
          {step === 'role' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-extrabold mb-1" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                Welcome to Handlenders
              </h2>
              <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
                Are you looking for talent or looking for work?
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  {
                    value: 'employer' as Role,
                    label: 'I hire talent',
                    sublabel: 'Employer / Recruiter',
                    icon: <Briefcase size={28} />,
                  },
                  {
                    value: 'worker' as Role,
                    label: 'I want work',
                    sublabel: 'Job Seeker / Candidate',
                    icon: <User size={28} />,
                  },
                ].map((opt) => (
                  <button
                    key={`role-${opt.value}`}
                    onClick={() => setRole(opt.value)}
                    className="p-5 rounded-2xl border-2 text-left transition-all duration-150 hover:shadow-sm"
                    style={{
                      borderColor: role === opt.value ? 'var(--charcoal)' : 'var(--border)',
                      background: role === opt.value ? 'var(--off-white)' : 'white',
                    }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                      style={{
                        background: role === opt.value ? 'var(--charcoal)' : 'var(--muted)',
                        color: role === opt.value ? 'var(--amber)' : 'var(--muted-foreground)',
                      }}
                    >
                      {opt.icon}
                    </div>
                    <p className="font-bold text-sm" style={{ color: role === opt.value ? 'var(--charcoal)' : 'var(--charcoal)' }}>
                      {opt.label}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{opt.sublabel}</p>
                  </button>
                ))}
              </div>

              <button onClick={() => setStep('identifier')} className="btn-primary w-full">
                Continue as {role === 'employer' ? 'Employer' : 'Candidate'}
                <ArrowRight size={18} />
              </button>

              <p className="text-xs text-center mt-4" style={{ color: 'var(--muted-foreground)' }}>
                New candidate?{' '}
                <Link href="/candidate" className="font-semibold hover:underline" style={{ color: 'var(--charcoal)' }}>
                  Apply to be verified — it&apos;s free
                </Link>
              </p>
            </div>
          )}

          {/* Step: Identifier */}
          {step === 'identifier' && (
            <div className="animate-fade-in">
              <button onClick={() => setStep('role')} className="text-sm font-medium mb-6 flex items-center gap-1 hover:underline" style={{ color: 'var(--muted-foreground)' }}>
                ← Back
              </button>
              <h2 className="text-2xl font-extrabold mb-1" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                Sign in as {role === 'employer' ? 'Employer' : 'Candidate'}
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                We&apos;ll send you a one-time code — no password needed.
              </p>

              {/* Identifier type toggle */}
              <div className="flex rounded-xl border border-border overflow-hidden mb-4">
                {(['email', 'phone'] as IdentifierType[]).map((type) => (
                  <button
                    key={`id-type-${type}`}
                    onClick={() => setIdentifierType(type)}
                    className="flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-150"
                    style={{
                      background: identifierType === type ? 'var(--charcoal)' : 'white',
                      color: identifierType === type ? 'var(--amber)' : 'var(--muted-foreground)',
                    }}
                  >
                    {type === 'email' ? <Mail size={15} /> : <Phone size={15} />}
                    {type === 'email' ? 'Email' : 'Phone'}
                  </button>
                ))}
              </div>

              <form onSubmit={identifierForm.handleSubmit(handleSendOtp)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                    {identifierType === 'email' ? 'Email address' : 'Mobile number'}{' '}
                    <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    Your {identifierType === 'email' ? 'email' : 'phone number'} is never shared with other users.
                  </p>
                  <div className="relative">
                    {identifierType === 'email'
                      ? <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                      : <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                    }
                    <input
                      type={identifierType === 'email' ? 'email' : 'tel'}
                      placeholder={identifierType === 'email' ? 'you@example.co.za' : '+27 82 000 0000'}
                      {...identifierForm.register('identifier', {
                        required: `${identifierType === 'email' ? 'Email' : 'Phone number'} is required`,
                        pattern: identifierType === 'email'
                          ? { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' }
                          : { value: /^(\+27|0)[0-9]{9}$/, message: 'Enter a valid South African mobile number' },
                      })}
                      className="input-field pl-9"
                    />
                  </div>
                  {identifierForm.formState.errors.identifier && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                      {identifierForm.formState.errors.identifier.message}
                    </p>
                  )}
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  {loading ? <><Loader2 size={16} className="animate-spin" />Sending code...</> : <>Send one-time code <ArrowRight size={16} /></>}
                </button>
              </form>

              <div className="mt-6 p-3 rounded-xl flex items-start gap-2" style={{ background: 'var(--muted)' }}>
                <ShieldCheck size={14} style={{ color: 'var(--charcoal)' }} className="flex-shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  We store only a secure hash of your identifier — never in plaintext. Your details are never visible to other users.
                </p>
              </div>
            </div>
          )}

          {/* Step: OTP */}
          {step === 'otp' && (
            <div className="animate-fade-in">
              <button onClick={() => setStep('identifier')} className="text-sm font-medium mb-6 flex items-center gap-1 hover:underline" style={{ color: 'var(--muted-foreground)' }}>
                ← Back
              </button>
              <h2 className="text-2xl font-extrabold mb-1" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                Enter your code
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                {identifierType === 'email'
                  ? <>We sent a sign-in link to <strong>{submittedIdentifier}</strong>. You can also enter the 6-digit token from the link.</>
                  : <>We sent a 6-digit code to <strong>{submittedIdentifier}</strong></>
                }
              </p>

              <form onSubmit={otpForm.handleSubmit(handleVerifyOtp)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>
                    6-digit code <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showOtp ? 'text' : 'password'}
                      placeholder="000000"
                      maxLength={6}
                      {...otpForm.register('otp', {
                        required: 'Please enter the code',
                        pattern: { value: /^[0-9]{6}$/, message: 'Enter the 6-digit code exactly' },
                      })}
                      className="input-field pr-10 text-2xl tracking-widest text-center tabular-nums font-bold"
                      style={{ letterSpacing: '0.3em' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowOtp(!showOtp)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                    >
                      {showOtp ? <EyeOff size={16} style={{ color: 'var(--muted-foreground)' }} /> : <Eye size={16} style={{ color: 'var(--muted-foreground)' }} />}
                    </button>
                  </div>
                  {otpForm.formState.errors.otp && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                      {otpForm.formState.errors.otp.message}
                    </p>
                  )}
                  {otpError && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{otpError}</p>
                  )}
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  {loading ? <><Loader2 size={16} className="animate-spin" />Verifying...</> : <>Verify &amp; Sign In</>}
                </button>
              </form>

              <button
                onClick={handleResendOtp}
                disabled={loading}
                className="mt-4 text-sm w-full text-center hover:underline disabled:opacity-50"
                style={{ color: 'var(--charcoal)' }}
              >
                Resend code
              </button>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="animate-fade-in text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--off-white)' }}>
                <CheckCircle size={32} style={{ color: 'var(--charcoal)' }} />
              </div>
              <h2 className="text-2xl font-extrabold mb-2" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                You&apos;re in!
              </h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Redirecting you to your {role === 'worker' ? 'candidate' : 'employer'} dashboard...
              </p>
              <div className="mt-4">
                <Loader2 size={20} className="animate-spin mx-auto" style={{ color: 'var(--charcoal)' }} />
              </div>
            </div>
          )}

          {/* New user link */}
          {(step === 'role' || step === 'identifier') && step !== 'role' && (
            <p className="text-xs text-center mt-6" style={{ color: 'var(--muted-foreground)' }}>
              New to Handlenders?{' '}
              <Link href="/candidate" className="font-semibold hover:underline" style={{ color: 'var(--charcoal)' }}>
                Apply to be verified
              </Link>
              {' · '}
              <Link href="/trust" className="hover:underline">Privacy</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}